import React, {ReactNode, useEffect, useCallback, useMemo} from 'react'
import {useNavigation} from '@react-navigation/native'
import {StackNavigationProp} from '@react-navigation/stack/lib/typescript/src/types'

import {LoanPaidOffTile} from 'src/products/loans/Dashboard/LoanPaidOffTile/LoanPaidOffTile'
import {LoanProgressTile} from 'src/products/loans/Dashboard/LoanProgressTile/LoanProgressTile'
import {LoanRejectedTile} from 'src/products/loans/Dashboard/LoanRejectedTile/LoanRejectedTile'
import {LoanExpiredTile} from 'src/products/loans/Dashboard/LoanExpiredTile/LoanExpiredTile'
import {LoanUnmetStateRequirementsTile} from 'src/products/loans/Dashboard/LoanUnmetStateRequirementsTile/LoanUnmetStateRequirementsTile'
import {LoanCancelledTile} from 'src/products/loans/Dashboard/LoanCancelledTile/LoanCancelledTile'
import {LoanEligibleToApplyTile} from 'src/products/loans/Dashboard/LoanEligibleToApplyTile/LoanEligibleToApplyTile'
import {LoanChargedOffSettledTile} from 'src/products/loans/Dashboard/LoanChargedOffSettledTile/LoanChargedOffSettledTile'
import {LoanHistoryTile} from 'src/products/loans/Dashboard/LoanHistoryTile/LoanHistoryTile'
import TileListView, {
  type ReactNodeWithStyle,
} from 'src/products/loans/components/molecules/TileListView/TileListView'
import {
  LoanClosedReasonCode,
  LoanOfferAvailabilityStatus,
  LoanTypeVariant,
  PrequalificationProduct,
} from '@possible/cassandra/src/types/types.mobile.generated'
import {isLoanClosedReasonCodePaidOff} from 'src/lib/loans/loansUtilsV2'
import {LoanPaidOff, TrackAppEvent} from 'src/lib/Analytics/analytics_compat'
import {AppEvents, CashAdvanceEvents, ManageActiveLoanEvents} from 'src/lib/Analytics/app_events'
import {usePrevious} from 'src/lib/utils/hooks'
import {
  shouldShowLinkAccountTile,
  shouldShowMembershipCancelledTile,
  shouldShowNextAdvanceTile,
  shouldShowLoanPaidOffTile,
  isReapplyOnDatePassed,
  shouldShowLoanEligibleToApplyTile,
  shouldShowAdvancePendingTile,
  shouldShowMembershipOverdueTile,
} from 'src/products/loans/Dashboard/DashboardLoanClosed/DashboardLoanClosed.utils'
import {DashboardLoanClosedProps} from 'src/products/loans/Dashboard/DashboardLoanClosed/DashboardLoanClosed.types'
import {usePromise} from 'src/lib/usePromise/usePromise'

import {useIsFeatureFlagEnabled} from 'src/lib/experimentation/useIsFeatureFlagEnabled'
import {LoanBrazeTiles} from 'src/products/loans/Dashboard/LoanBrazeTiles/LoanBrazeTiles'
import {useCassandraMutation, useCassandraSubscription} from '@possible/cassandra/src/utils/hooks'
import {ReapplicationConfirmBankDocument} from 'src/products/MCU/AccountManagementV2/PaymentMethods/BankAggregator/ReapplicationBank/ReapplicationBank.gqls'
import {logAddPaymentMethodError} from 'src/products/general/GeneralPaymentMethods/GeneralPaymentMethods.utils'
import {NextAdvanceTile} from 'src/products/advance/Dashboard/NextAdvanceTile/NextAdvanceTile'
import {
  LinkAccountTile,
  LinkAccountTileVariant,
} from 'src/designSystem/components/molecules/LinkAccountTile/LinkAccountTile'
import {useAccountManagementBankLinking} from 'src/products/MCU/AccountManagementV2/useAccountManagementBankLinking'
import {MainStackParamList} from 'src/nav/MainStackParamsList'
import {AdvancePendingTile} from 'src/designSystem/components/molecules/AdvancePendingTile/AdvancePendingTile'
import {MembershipCanceledTile} from 'src/products/advance/Dashboard/MembershipCanceledTile/MembershipCanceledTile'
import {MembershipOverdueTile} from 'src/products/advance/Dashboard/MembershipOverdueTile/MembershipOverdueTile'
import {PreQualAmountDocument} from 'src/products/loans/Dashboard/DashboardLoanClosed/queries/PrequalAmountSubscription.gqls'
import Log from 'src/lib/loggingUtil'
import Spinner from 'src/products/general/components/atoms/Spinner/Spinner'

/**
 * Dashboard displayed when a loan is closed and paid off.
 */
const DashboardLoanClosed: React.FC<DashboardLoanClosedProps> = (props) => {
  const {
    reason,
    reapplyOn,
    reapplyOnDays,
    prequalAmount: loanPrequalAmount,
    payments,
    userEmailAddress,
    hasPreviousLoan: hasPreviousLoanOrAdvance,
    disbursement,
    onApplyForNewLoan,
    onShowDefaultExplanation: handleOnShowDefaultExplanation,
    onShowChargedOffExplanation: handleOnShowChargedOffExplanation,
    onContactUs: handleOnContactUs,
    onLoanRejectedLearnMore: handleOnLoanRejectedLearnMore,
    onViewStateDatabasePolicy: handleOnViewStateDatabasePolicy,
    onViewLoanHistory: handleOnViewLoanHistory,
    loanTypeVariant,
    subscriptions,
    loanOffers,
    isAdvance,
    contUwAssessmentDate,
    linkedAccountsData,
    onResumeMembership,
    isResumeMembershipLoading,
    onAcceptSubscriptionAgreement: handleOnAcceptSubscriptionAgreement,
    refetchDashboardData,
    onMakeAPayment: handleOnMakeAPayment,
  } = props

  const product = isAdvance ? PrequalificationProduct.CashAdvance : PrequalificationProduct.Loan

  const {data: preQualAmountSubscriptionData, loading: isLoadingSubscriptionData} =
    useCassandraSubscription(PreQualAmountDocument, {
      variables: {
        product,
      },
      skip: isAdvance === false,
      onError: (error) => {
        Log.error(error, 'Error fetching prequal amount subscription data')
      },
    })

  const advancePrequalAmount = preQualAmountSubscriptionData?.loanPrequalification?.amount

  const prequalAmount = isAdvance ? advancePrequalAmount : loanPrequalAmount

  const navigation = useNavigation<StackNavigationProp<MainStackParamList>>()

  const availableOffer = loanOffers.find(
    (offer) => offer.loanOfferAvailabilityStatus === LoanOfferAvailabilityStatus.OfferAvailable,
  )

  const hasStandardLoanOffer: boolean =
    availableOffer?.offers[0].loanTypeVariant !== LoanTypeVariant.NonRecourseInstallment

  const reapplyOnDateTime = new Date(reapplyOn)
  const previousCloseReason = usePrevious(reason)
  const {addOrRelinkAccountForPrimary} = useAccountManagementBankLinking()

  useEffect(() => {
    if (previousCloseReason !== reason) {
      if (isLoanClosedReasonCodePaidOff(reason)) {
        // track when a loan is paid off
        LoanPaidOff()
      }
      // the first time a close reason is viewed we track an analytics event for that particular close reason
      TrackAppEvent(
        ManageActiveLoanEvents.closed_dashboard_viewed,
        AppEvents.Category.ManageActiveLoan,
        {
          reason,
        },
      )
    }
  }, [previousCloseReason, reason])

  const isAfterReapplyOnDate = isReapplyOnDatePassed(reapplyOn)

  const isContentCardsEnabled = useIsFeatureFlagEnabled('braze-content-cards')

  const [confirmBank] = useCassandraMutation(ReapplicationConfirmBankDocument)

  const preferredAccount = linkedAccountsData?.preferredAccount
  const latestAccount = linkedAccountsData?.latestAccount

  const subscriptionOverdueAmount = useMemo(() => {
    try {
      return parseFloat(subscriptions?.current?.payments.overdueAmount ?? '0.00')
    } catch {
      return 0.0
    }
  }, [subscriptions])

  const tiles: (ReactNode | ReactNodeWithStyle)[] = []

  const [handleOnApply, {isLoading: isApplyLoading}] = usePromise(
    async (sourceForAnalytics: string | undefined): Promise<void> => {
      TrackAppEvent(
        AppEvents.Name.link_bank_account_current_account_selected,
        AppEvents.Category.Application,
      )

      if (isAdvance === true) {
        const accountId = preferredAccount?.id || latestAccount?.id

        if (!accountId) {
          throw new Error('No preferred or latest account found')
        }

        try {
          await confirmBank({variables: {accountId}})
          await onApplyForNewLoan({sourceForAnalytics})
        } catch (e) {
          logAddPaymentMethodError(e, 'Error while continuing with current account')
        }
      } else {
        await onApplyForNewLoan({sourceForAnalytics})
      }
    },
  )

  const handleGetAdvanceFromMembershipCancelledTile = useCallback(async (): Promise<void> => {
    await handleOnApply('DashboardLoanClosed-MembershipCanceledTile-GetAdvance')
  }, [handleOnApply])

  const onLinkAccount = (accountId?: string): void => {
    const accountToRelink = accountId ? {accountToRelink: {id: accountId}} : {}
    TrackAppEvent(
      AppEvents.Name.link_primary_bank_account_selected,
      AppEvents.Category.BankAccountManagement,
      {
        source: 'DashboardLoanClosed_LinkAccountTile',
      },
    )

    addOrRelinkAccountForPrimary({
      ...accountToRelink,
      onChoosePrimaryAccountComplete: (): Promise<void> => {
        TrackAppEvent(
          AppEvents.Name.link_primary_bank_account_successful,
          AppEvents.Category.BankAccountManagement,
          {
            source: 'DashboardLoanClosed_LinkAccountTile',
          },
        )
        navigation.navigate('Dashboard')
        return Promise.resolve()
      },
    })
  }

  const handleOnResumeMembership = async (): Promise<void> => {
    TrackAppEvent(CashAdvanceEvents.cash_resume_membership_selected, AppEvents.Category.CashAdvance)

    await onResumeMembership()
  }

  // Helper to add content card tile if applicable
  const addContentCardTile = (): void => {
    const contentTile = renderContentCardTile()
    if (contentTile) {
      tiles.push(contentTile)
    }
  }

  // Helper to render the content card tile if the feature flag is enabled
  const renderContentCardTile = (): ReactNodeWithStyle | null => {
    return isContentCardsEnabled
      ? {element: <LoanBrazeTiles location={3} includeMarginTop />, style: {marginTop: 0}}
      : null
  }

  // Helper to render the membership overdue tile (for Cash Advance users only)
  const renderMembershipOverdueTile = (): ReactNode => (
    <MembershipOverdueTile
      testID="DashboardLoanClosed-MembershipOverdueTile"
      amount={subscriptionOverdueAmount}
      onPayMembership={handleOnMakeAPayment}
      subscriptionStatus={subscriptions?.current?.statusData?.status}
      subscriptionSubStatus={subscriptions?.current?.statusData?.substatus}
    />
  )

  // Helper to render the cancelled membership tile (for Cash Advance users only)
  const renderMembershipCancelledTile = (): ReactNode => (
    <MembershipCanceledTile
      testID="DashboardLoanClosed-MembershipCanceledTile"
      onReactivateMembership={handleOnResumeMembership}
      isResumeMembershipLoading={isResumeMembershipLoading}
      onGetAdvance={handleGetAdvanceFromMembershipCancelledTile}
      subscriptionStatus={subscriptions?.current?.statusData.status}
      subscriptionSubStatus={subscriptions?.current?.statusData?.substatus}
      finalMembershipFeeDate={subscriptions?.current?.payments.next?.scheduledAt}
      prequalAmount={prequalAmount ?? undefined}
      assessmentDate={contUwAssessmentDate}
      loanId={''}
      onAcceptSubscriptionAgreement={handleOnAcceptSubscriptionAgreement}
      refetchDashboardData={refetchDashboardData}
      reason={reason}
    />
  )

  // Helper to render the Link Account tile (for Cash Advance users only)
  const renderLinkAccountTile = (): ReactNode => (
    <LinkAccountTile
      testID="DashboardLoanClosed-LinkAccountTile"
      variant={LinkAccountTileVariant.Unlinked} //TODO: Add dynamic variant based on the reason after we have the cont-uw graphQL subscription
      isAdvance={isAdvance}
      handleOnLinkAccount={() => onLinkAccount(preferredAccount?.id)}
    />
  )

  // Helper to render the Advance Pending tile (for Cash Advance users only)
  const renderAdvancePendingTile = (): ReactNode => (
    <AdvancePendingTile
      daysUntilCanReapply={reapplyOnDays}
      testID="DashboardLoanClosed-AdvancePendingTile"
    />
  )

  // Helper to render the Next Advance tile (for Cash Advance users only)
  const renderNextAdvanceTile = (): ReactNode => (
    <NextAdvanceTile
      onApplyForNextAdvance={async (): Promise<void> => {
        TrackAppEvent(CashAdvanceEvents.cash_next_advance_selected, AppEvents.Category.CashAdvance)
        await handleOnApply('DashboardLoanClosed-NextAdvanceTile')
      }}
      testID="DashboardLoanClosed-NextAdvanceTile"
      amount={prequalAmount ? parseFloat(prequalAmount) : undefined}
      assessmentDate={contUwAssessmentDate}
    />
  )

  // Helper to render Loan Paid Off tile for Loans users (for Loans users only)
  const renderLoanPaidOffTile = (): ReactNode => (
    <LoanPaidOffTile
      hasLoanOffer={hasStandardLoanOffer}
      isAdvance={isAdvance}
      isBusy={isApplyLoading}
      onApplyForNewLoan={async (): Promise<void> => {
        await handleOnApply('DashboardLoanClosed-LoanPaidOffTile')
      }}
      reapplyOnDate={reapplyOnDateTime}
      testID="DashboardLoanClosed-LoanPaidOffTile"
    />
  )

  // Helper to render Loan Eligible To Apply tile
  const renderLoanEligibleToApplyTile = (): ReactNode => {
    const testID =
      isAdvance === true
        ? 'DashboardLoanClosed-Advance-EligibleToApplyTile'
        : 'DashboardLoanClosed-LoanEligibleToApplyTile'

    const sourceId =
      isAdvance === true
        ? 'DashboardLoanClosed-Advance-EligibleToApplyTile'
        : 'DashboardLoanClosed-LoanEligibleToApplyTile'

    const prequalifiedAmount =
      isAdvance === true ? undefined : prequalAmount ? parseFloat(prequalAmount) : undefined

    const handleOnApplyForNewLoan = async (): Promise<void> => {
      if (isAdvance === true) {
        TrackAppEvent(CashAdvanceEvents.cash_next_advance_selected, AppEvents.Category.CashAdvance)
      }
      await handleOnApply(sourceId)
    }

    return (
      <LoanEligibleToApplyTile
        testID={testID}
        prequalifiedAmount={prequalifiedAmount}
        onApplyForNewLoan={handleOnApplyForNewLoan}
        isLoading={isApplyLoading}
        isAdvance={isAdvance}
        hasLoanOffer={hasStandardLoanOffer}
      />
    )
  }

  // Helper to render Loan Progress tile
  const renderLoanProgressTile = (): ReactNode => (
    <LoanProgressTile
      testID="DashboardLoanClosed-LoanProgressTile"
      payments={payments}
      fundingFailed={false} //not used when loan is closed since we only show this if its fully paid off
      amountProcessing="0.00" //a closed loan will never have any amount processing / in progress
      noAccountLinked={false} //same as fundingFailed
      onShowDefaultExplanation={handleOnShowDefaultExplanation}
      onShowChargedOffExplanation={handleOnShowChargedOffExplanation}
      onReschedule={() => {}} //when a loan is closed it cant be rescheduled so this isn't necessary
      onContactUs={handleOnContactUs}
      onRelinkBankAccount={() => {}} //when a loan is closed there is no UX to relink a bank account so this isn't necessary
      isAdvance={isAdvance}
    />
  )

  const renderLoanHistoryTile = (): ReactNode | null =>
    // this should always exist on a paid off loan but per the type it may be undefined so we need a safety check to please TypeScript
    disbursement ? (
      <LoanHistoryTile
        loanPayments={payments}
        hasPreviousLoan={hasPreviousLoanOrAdvance}
        onViewLoanHistory={handleOnViewLoanHistory}
        loanDisbursement={disbursement}
        testID="DashboardLoanClosed-LoanHistoryTile"
        loanTypeVariant={loanTypeVariant}
        subscriptions={subscriptions}
        isAdvance={isAdvance === true}
      />
    ) : null

  // Helper to render Loan Rejected tile
  const renderLoanRejectedTile = (): ReactNode => (
    <LoanRejectedTile
      testID="DashboardLoanClosed-LoanRejectedTile"
      onLoanRejectedLearnMore={handleOnLoanRejectedLearnMore}
      userEmailAddress={userEmailAddress}
      reapplyOnDate={reapplyOnDateTime}
      isAdvance={isAdvance === true}
      isForRepeatAdvance={hasPreviousLoanOrAdvance}
    />
  )

  // Helper to render Loan Expired tile
  const renderLoanExpiredTile = (): ReactNode => (
    <LoanExpiredTile
      wasApproved={reason === LoanClosedReasonCode.LoanExpired}
      testID="DashboardLoanClosed-LoanExpiredTile"
      onApplyForNewLoan={async (): Promise<void> => {
        await handleOnApply('DashboardLoanClosed-LoanExpiredTile')
      }}
      isBusy={isApplyLoading}
      isAdvance={isAdvance === true}
      hasLoanOffer={hasStandardLoanOffer}
    />
  )

  // Helper to render Loan Unmet State Requirements tile
  const renderLoanUnmetStateRequirementsTile = (): ReactNode => (
    <LoanUnmetStateRequirementsTile
      testID="DashboardLoanClosed-LoanUnmetStateRequirementsTile"
      onApplyAgain={async (): Promise<void> => {
        await handleOnApply('DashboardLoanClosed-LoanUnmetStateRequirementsTile')
      }}
      onViewStateDatabasePolicy={handleOnViewStateDatabasePolicy}
      isBusy={isApplyLoading}
      hasLoanOffer={hasStandardLoanOffer}
    />
  )

  // Helper to render Loan Cancelled tile
  const renderLoanCancelledTile = (): ReactNode => (
    <LoanCancelledTile
      testID="DashboardLoanClosed-LoanCancelledTile"
      onApplyForNewLoan={async (): Promise<void> => {
        await handleOnApply('DashboardLoanClosed-LoanCancelledTile')
      }}
      isBusy={isApplyLoading}
      hasLoanOffer={hasStandardLoanOffer}
    />
  )

  // Helper to render Loan Charged Off Settled tile
  const renderLoanChargedOffSettledTile = (): ReactNode => (
    <LoanChargedOffSettledTile
      hasLoanOffer={hasStandardLoanOffer}
      isBusy={isApplyLoading}
      onApplyForNewLoan={async (): Promise<void> => {
        await handleOnApply('DashboardLoanClosed-LoanChargedOffSettledTile')
      }}
      reapplyOnDate={reapplyOnDateTime}
      testID="DashboardLoanClosed-LoanChargedOffSettledTile"
    />
  )

  if (shouldShowMembershipOverdueTile({isAdvance, subscriptions})) {
    tiles.push(renderMembershipOverdueTile())
  }

  // MembershipCancelledTile
  if (shouldShowMembershipCancelledTile({isAdvance, subscriptions})) {
    tiles.push(renderMembershipCancelledTile())
  }

  // LinkAccountTile
  if (shouldShowLinkAccountTile({isAdvance, preferredAccount})) {
    tiles.push(renderLinkAccountTile())
  }

  // AdvancePendingTile
  if (shouldShowAdvancePendingTile({reason, isAdvance, isAfterReapplyOnDate})) {
    tiles.push(renderAdvancePendingTile())
  }

  // NextAdvanceTile
  if (
    shouldShowNextAdvanceTile({
      reason,
      isAdvance,
      prequalAmount,
      isAfterReapplyOnDate,
      subscriptions,
    })
  ) {
    tiles.push(renderNextAdvanceTile())
  }
  // LoanPaidOffTile
  if (shouldShowLoanPaidOffTile({reason, isAdvance, prequalAmount, isAfterReapplyOnDate})) {
    tiles.push(renderLoanPaidOffTile())
  }

  // LoanEligibleToApplyTile
  if (
    shouldShowLoanEligibleToApplyTile({
      reason,
      isAdvance,
      isAfterReapplyOnDate,
      prequalAmount,
      subscriptions,
      preferredAccount,
    })
  ) {
    tiles.push(renderLoanEligibleToApplyTile())
  }

  // Render additional tiles based on the close reason
  switch (reason) {
    case LoanClosedReasonCode.Paidoff:
    case LoanClosedReasonCode.ChargedoffPaidoff: {
      addContentCardTile()

      if (isAdvance === false) {
        tiles.push(renderLoanProgressTile())
      }

      const historyTile = renderLoanHistoryTile()
      if (historyTile) {
        tiles.push(historyTile)
      }
      break
    }
    case LoanClosedReasonCode.Rejected:
      if (isAfterReapplyOnDate) {
        addContentCardTile()
      } else {
        tiles.push(renderLoanRejectedTile())
      }
      break
    case LoanClosedReasonCode.LoanExpired:
    case LoanClosedReasonCode.DecisioningExpired: {
      tiles.push(renderLoanExpiredTile())
      addContentCardTile()
      break
    }
    case LoanClosedReasonCode.UnmetStateRequirement:
      if (!isAfterReapplyOnDate) {
        tiles.push(renderLoanUnmetStateRequirementsTile())
      }
      break
    case LoanClosedReasonCode.Cancelled:
      tiles.push(renderLoanCancelledTile())
      break
    case LoanClosedReasonCode.ChargedoffSettled: {
      tiles.push(renderLoanChargedOffSettledTile())
      addContentCardTile()
      break
    }
    case LoanClosedReasonCode.Other:
      throw new Error('"Other" close reason not implemented')
  }

  if (isLoadingSubscriptionData) {
    return <Spinner />
  }

  return <TileListView testID="DashboardLoanClosed" list={tiles} />
}

export {DashboardLoanClosed}
